import { PreciseNumber } from '@fcom/core/utils';

import { CartLineItem } from './cart-line-items.utils';

/**
 * One unit-quantity change for a single line-item key. `quantity` is always positive (the magnitude of the
 * change); which side it's on is determined by the `added`/`removed` bucket it lives in.
 */
export interface SelectionDeltaEntry {
  key: string;
  paxId: string;
  quantity: number;
  unitPrice: PreciseNumber;
}

/**
 * The difference between the local desired selection and the committed cart, expressed as units added
 * (present locally beyond what's committed) and units removed (committed beyond what's kept locally).
 */
export interface SelectionDelta {
  added: SelectionDeltaEntry[];
  removed: SelectionDeltaEntry[];
}

const indexByKey = (items: CartLineItem[]): Map<string, CartLineItem> => new Map(items.map((item) => [item.key, item]));

/**
 * Quantity-aware diff of the local desired state against the committed cart, per line-item key.
 *
 * - Added units carry the local (catalog) price — that's what not-yet-committed units cost.
 * - Removed units carry the committed price — that's what dropping them refunds.
 * - Equal quantities produce no entry: the committed price is trusted even if the catalog price drifted.
 * - Benefits (included in ticket type / tier) never contribute to the payable delta.
 *
 * Combo displacement falls out for free: adding a combo appears as an `added` entry under its own key,
 * while the standalone products it cleared appear as `removed` entries under theirs — no displacement map
 * is needed here.
 */
export const diffLineItems = (committed: CartLineItem[], local: CartLineItem[]): SelectionDelta => {
  const committedByKey = indexByKey(committed);
  const localByKey = indexByKey(local);
  const added: SelectionDeltaEntry[] = [];
  const removed: SelectionDeltaEntry[] = [];

  new Set([...committedByKey.keys(), ...localByKey.keys()]).forEach((key) => {
    const committedItem = committedByKey.get(key);
    const localItem = localByKey.get(key);

    if (committedItem?.isBenefit || localItem?.isBenefit) {
      return;
    }

    const committedQty = committedItem?.quantity ?? 0;
    const localQty = localItem?.quantity ?? 0;

    if (localQty > committedQty) {
      added.push({
        key,
        paxId: localItem.paxId,
        quantity: localQty - committedQty,
        unitPrice: localItem.unitPrice,
      });
    } else if (committedQty > localQty) {
      removed.push({
        key,
        paxId: committedItem.paxId,
        quantity: committedQty - localQty,
        unitPrice: committedItem.unitPrice,
      });
    }
  });

  return { added, removed };
};

const sumEntries = (entries: SelectionDeltaEntry[]): PreciseNumber =>
  entries.reduce(
    (sum, { unitPrice, quantity }) => sum.add(unitPrice.multiply(PreciseNumber.from(quantity, 0))),
    PreciseNumber.ZERO
  );

/**
 * Applies a selection delta on top of an authoritative committed base:
 * `base + Σ(added.unitPrice * qty) − Σ(removed.unitPrice * qty)`.
 *
 * The committed base stays authoritative (it carries cart-level taxes, fees and per-pax splits that can't
 * be reproduced from catalog prices); only the service-level delta is applied on top.
 */
export const applyDelta = (base: PreciseNumber, delta: SelectionDelta): PreciseNumber =>
  base.add(sumEntries(delta.added)).minus(sumEntries(delta.removed));

/**
 * Narrows a delta to the entries belonging to the given passengers, so a per-passenger total can be
 * computed by applying the filtered delta on that passenger's committed base. Pass the passenger and any
 * accompanying infant id together.
 */
export const filterDeltaByPax = (delta: SelectionDelta, paxIds: string[]): SelectionDelta => {
  const ids = new Set(paxIds);
  return {
    added: delta.added.filter((entry) => ids.has(entry.paxId)),
    removed: delta.removed.filter((entry) => ids.has(entry.paxId)),
  };
};
